import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  RefreshCw,
  AlertCircle,
  User,
  X,
  Save,
  Check,
  Copy,
  Download
} from 'lucide-react';
import { useAuth } from '@/store/hooks';
import { downloadMembersExcel } from '@/services/exportService';

interface Member {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  whatsapp_number?: string;
  role: 'universal_admin' | 'office_admin' | 'department_head' | 'front_worker' | 'member';
  status: 'active' | 'inactive' | 'suspended';
  member_id?: string;
  block_number?: string;
  flat_number?: string;
  address?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  date_of_birth?: string;
  membership_date?: string;
  expiry_date?: string;
  is_committee_member: boolean;
  is_department_head?: boolean;
  committee_position?: string;
  committee_description?: string;
  department?: {
    id: string;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

// interface Department {
//   id: string;
//   name: string;
//   code: string;
// }

interface Department {
  id: string;
  name: string;
  code: string;
}

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  whatsapp_number: string;
  password: string;
  status: 'active' | 'inactive' | 'suspended';
  member_id: string;
  block_number: string;
  house_number: string;
  floor_number: string;
  address: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  date_of_birth: string;
  membership_date: string;
  expiry_date: string;
  is_committee_member: boolean;
  is_department_head: boolean;
  committee_position: string;
  committee_description: string;
  role: 'member' | 'department_head';
  department_ids: string[];
}

interface FormErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  password?: string;
  date_of_birth?: string;
  membership_date?: string;
  expiry_date?: string;
  role?: string;
  general?: string;
}

const COMMITTEE_ROLES = [
  'President',
  'Vice President', 
  'General Secretary',
  'Treasurer',
  'Secretary',
  'M.C. Members'
];

/** Last day of current financial year (India: FY April–March = 31 March). Used as default expiry for new members. */
function getLastDayOfCurrentFinancialYear(): string {
  const now = new Date();
  const endYear = now.getMonth() < 3 ? now.getFullYear() : now.getFullYear() + 1;
  const d = new Date(endYear, 2, 31);
  return d.toISOString().slice(0, 10);
}

const MembersPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [members, setMembers] = useState<Member[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  /** Filter members assigned to this department (via user_departments). */
  const [departmentFilterId, setDepartmentFilterId] = useState('');
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50); // Show 50 per page
  const [totalMembers, setTotalMembers] = useState(0);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    whatsapp_number: '',
    password: '',
    status: 'active',
    member_id: '',
    block_number: '',
    house_number: '',
    floor_number: '',
    address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    date_of_birth: '',
    membership_date: '',
    expiry_date: '',
    is_committee_member: false,
    is_department_head: false,
    committee_position: '',
    committee_description: '',
    role: 'member',
    department_ids: []
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [whatsappSameAsPhone, setWhatsappSameAsPhone] = useState(false);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const deepLinkEditHandled = useRef<string | null>(null);
  const deepLinkCreateHandled = useRef(false);

  // Check if user is universal admin
  const isUniversalAdmin = user?.role === 'universal_admin';

  // Sync department filter from URL (?department_id=) — e.g. from Departments → List / Add member
  useEffect(() => {
    const d = searchParams.get('department_id');
    setDepartmentFilterId(d ?? '');
  }, [searchParams]);

  useEffect(() => {
    if (isUniversalAdmin) {
      fetchMembers();
      fetchDepartments();
    }
  }, [isUniversalAdmin]);

  // Fetch members when filters or pagination changes
  useEffect(() => {
    if (isUniversalAdmin) {
      fetchMembers();
    }
  }, [currentPage, pageSize, roleFilter, statusFilter, departmentFilterId, isUniversalAdmin]);

  useEffect(() => {
    setCurrentPage(1);
  }, [departmentFilterId]);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    const timer = setTimeout(() => {
      if (isUniversalAdmin) {
        setCurrentPage(1); // Reset to first page on new search
        fetchMembers();
      }
    }, 500); // Wait 500ms after user stops typing

    setSearchDebounceTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [searchTerm]);

  // Keep WhatsApp number in sync with phone when tick button is active (for form submission)
  useEffect(() => {
    if (whatsappSameAsPhone && formData.phone) {
      setFormData(prev => {
        if (prev.whatsapp_number !== prev.phone) {
          return {
            ...prev,
            whatsapp_number: prev.phone
          };
        }
        return prev;
      });
    }
  }, [whatsappSameAsPhone, formData.phone]);


  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      await downloadMembersExcel({
        search: searchTerm.trim() || undefined,
        role: roleFilter !== 'all' ? roleFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        department_id: departmentFilterId || undefined
      });
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export members to Excel');
    } finally {
      setIsExporting(false);
    }
  };

  const fetchMembers = async () => {
    setIsLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());
      
      // Add search parameter if provided
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      // Add role filter if not 'all'
      if (roleFilter !== 'all') {
        params.append('role', roleFilter);
      }
      
      // Add status filter if not 'all'
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      if (departmentFilterId) {
        params.append('department_id', departmentFilterId);
      }

      const response = await fetch(`/api/v1/users?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const result = data.data || {};
        const users = result.users || [];
        
        // Exclude universal_admin from the member list
        const filteredUsers = users.filter((member: Member) => member.role !== 'universal_admin');
        
        setMembers(filteredUsers);
        
        // Use total from backend and adjust for universal_admin exclusion
        // The backend total includes universal_admin, so we need to estimate the adjusted total
        const universalAdminCountOnPage = users.length - filteredUsers.length;
        const backendTotal = result.total || 0;
        
        // If we're on the first page and filtered out universal_admin, adjust the total
        // For other pages, we can't know exactly, so use the backend total
        if (universalAdminCountOnPage > 0 && currentPage === 1) {
          // Estimate: if we see X universal_admin on first page, estimate total universal_admin
          // This is approximate - ideally backend should exclude them
          const estimatedUniversalAdminTotal = Math.ceil((universalAdminCountOnPage / users.length) * backendTotal);
          setTotalMembers(Math.max(0, backendTotal - estimatedUniversalAdminTotal));
        } else {
          setTotalMembers(backendTotal);
        }
        
        console.log('Members fetched:', {
          totalMembers: totalMembers,
          currentPage,
          pageSize,
          totalPages: Math.ceil((result.total || 0) / pageSize),
          usersOnPage: filteredUsers.length
        });
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetch('/api/v1/departments', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDepartments(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  // Calculate pagination info
  const totalPages = Math.max(1, Math.ceil(totalMembers / pageSize));
  const startIndex = totalMembers > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endIndex = totalMembers > 0 ? Math.min(currentPage * pageSize, totalMembers) : 0;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    // Validate department selection for department head
    // Note: If no departments are selected, the role will be automatically changed to 'member' in handleSubmit
    // So we only show a warning, not an error
    if ((formData.is_department_head || formData.role === 'department_head') && formData.department_ids.length === 0) {
      // This is now handled automatically - role will be changed to member if no departments
      // We can show a warning but not block submission
    }

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    // Password is not required on create - backend uses default nfcrwa@123

    if (!formData.date_of_birth.trim()) {
      newErrors.date_of_birth = 'Date of birth is required';
    } else {
      // Validate date format (ISO8601)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formData.date_of_birth)) {
        newErrors.date_of_birth = 'Please enter a valid date (YYYY-MM-DD)';
      }
    }

    if (!formData.membership_date.trim()) {
      newErrors.membership_date = 'Membership date is required';
    } else {
      // Validate date format (ISO8601)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formData.membership_date)) {
        newErrors.membership_date = 'Please enter a valid date (YYYY-MM-DD)';
      }
    }

    if (!formData.expiry_date.trim()) {
      newErrors.expiry_date = 'Expiry date is required';
    } else {
      // Validate date format (ISO8601)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formData.expiry_date)) {
        newErrors.expiry_date = 'Please enter a valid date (YYYY-MM-DD)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // If phone number changes and tick button is active, update WhatsApp number
      if (field === 'phone' && whatsappSameAsPhone) {
        updated.whatsapp_number = value;
      }
      
      return updated;
    });
    
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const copyPhoneToWhatsApp = () => {
    if (formData.phone) {
      setFormData(prev => ({
        ...prev,
        whatsapp_number: prev.phone
      }));
      setWhatsappSameAsPhone(true);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const url = modalMode === 'create' 
        ? '/api/v1/users' 
        : `/api/v1/users/${editingMember?.id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';

      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        setErrors({
          general: 'Authentication required. Please login again.'
        });
        setIsSubmitting(false);
        return;
      }

      // Prepare data for submission - map house_number to flat_number for backend
      const { house_number, floor_number, department_ids, ...restFormData } = formData;
      
      // If no departments selected but user is marked as department head, change to member
      const shouldBeDepartmentHead = (formData.is_department_head || formData.role === 'department_head') && department_ids.length > 0;
      const finalRole = shouldBeDepartmentHead ? 'department_head' : 'member';
      const finalIsDepartmentHead = shouldBeDepartmentHead;
      
      const submitData: any = { 
        ...restFormData,
        flat_number: house_number, // Map house_number to flat_number
        role: finalRole, // Synchronized with is_department_head and department_ids
        is_committee_member: formData.is_committee_member || false, // Ensure boolean is always sent
        is_department_head: finalIsDepartmentHead // Ensure boolean is always sent
      };
      
      // Remove floor_number if not needed by backend
      delete submitData.floor_number;
      
      // Ensure whatsapp_number is set if tick button was used
      if (whatsappSameAsPhone && formData.phone) {
        submitData.whatsapp_number = formData.phone;
      }
      
      // Remove empty optional fields to avoid validation errors
      if (!submitData.whatsapp_number || submitData.whatsapp_number.trim() === '') {
        delete submitData.whatsapp_number;
      }
      if (!submitData.emergency_contact_name || submitData.emergency_contact_name.trim() === '') {
        delete submitData.emergency_contact_name;
      }
      if (!submitData.emergency_contact_phone || submitData.emergency_contact_phone.trim() === '') {
        delete submitData.emergency_contact_phone;
      }
      
      // Member ID will be auto-generated by backend in sequential format (NFCR0001, NFCR0002, etc.)
      // Remove member_id from submitData - it's auto-generated on create and cannot be changed on edit
      delete submitData.member_id;
      
      // Password: on create we don't send (backend uses default nfcrwa@123); on edit only send if provided
      if (modalMode === 'create') {
        delete submitData.password;
      } else if (!submitData.password) {
        delete submitData.password;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        const data = await response.json();
        const userId = editingMember?.id || data.data?.id;
        
        // Update departments based on final role
        if (finalRole === 'department_head' && userId && department_ids.length > 0) {
          // User is department head with departments - assign them
          try {
            const deptResponse = await fetch(`/api/v1/staff/${userId}/departments`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ department_ids })
            });
            
            if (!deptResponse.ok) {
              const errorData = await deptResponse.json().catch(() => ({}));
              console.error('Failed to assign departments:', errorData);
            }
          } catch (error) {
            console.error('Error assigning departments:', error);
          }
        } else if (userId && (editingMember?.role === 'department_head' || editingMember?.is_department_head)) {
          // User was department head but is no longer (either role changed or all departments removed)
          // Remove all departments
          try {
            const deptResponse = await fetch(`/api/v1/staff/${userId}/departments`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ department_ids: [] })
            });
            
            if (!deptResponse.ok) {
              console.error('Failed to remove departments');
            }
          } catch (error) {
            console.error('Error removing departments:', error);
          }
        }
        
        // Update the member in the list immediately with the response data
        if (modalMode === 'edit' && data.data) {
          setMembers(prev => prev.map(member => 
            member.id === editingMember?.id ? { ...member, ...data.data } : member
          ));
        }
        
        // Refresh the members list to ensure all data is up to date
        await fetchMembers();
        
        closeModal();
      } else {
        let errorMessage = 'Failed to save member';
        const backendErrors: FormErrors = {};
        
        try {
          const errorData = await response.json();
          
          // Handle validation errors from backend
          if (errorData.errors && Array.isArray(errorData.errors)) {
            errorData.errors.forEach((error: any) => {
              if (error.path && error.msg) {
                // Map backend field names to form field names
                const fieldName = error.path as keyof FormErrors;
                backendErrors[fieldName] = error.msg;
              }
            });
          }
          
          errorMessage = errorData.message || errorData.error || errorMessage;
          
          // Handle authentication errors
          if (response.status === 401) {
            errorMessage = 'Authentication failed. Please login again.';
            // Optionally redirect to login
            // window.location.href = '/login';
          }
        } catch (parseError) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        // Set both field-specific errors and general error
        setErrors({
          ...backendErrors,
          general: Object.keys(backendErrors).length > 0 ? 'Please fix the errors below' : errorMessage
        });
      }
    } catch (error) {
      console.error('Error saving member:', error);
      setErrors({
        general: 'Network error. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to format date for date input (YYYY-MM-DD)
  const formatDateForInput = (dateString: string | undefined): string => {
    if (!dateString) return '';
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // If in ISO8601 format with time, extract date part
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    } catch (e) {
      // If parsing fails, return empty string
    }
    return '';
  };

  const handleEdit = async (member: Member) => {
    setEditingMember(member);
    setModalMode('edit');
    const whatsappNumber = member.whatsapp_number || '';
    const phoneNumber = member.phone || '';
    const isSame = whatsappNumber === phoneNumber && whatsappNumber !== '';
    
    // Fetch user departments if member is department_head
    let userDepartmentIds: string[] = [];
    if (member.role === 'department_head' || member.is_department_head) {
      try {
        const response = await fetch(`/api/v1/staff/${member.id}/departments`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const deptData = await response.json();
          userDepartmentIds = deptData.data?.map((d: any) => d.id) || [];
        }
      } catch (error) {
        console.error('Error fetching user departments:', error);
      }
    }
    
    // Ensure is_department_head is properly set based on role or flag
    const isDepartmentHead = member.is_department_head || member.role === 'department_head';
    
    setFormData({
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      email: member.email || '',
      phone: phoneNumber,
      whatsapp_number: whatsappNumber,
      password: '', // Don't pre-fill password
      status: member.status || 'active',
      member_id: member.member_id || '', // Keep member_id for display
      block_number: member.block_number || '',
      house_number: member.flat_number || '', // Map flat_number to house_number
      floor_number: '', // New field
      address: member.address || '',
      emergency_contact_name: member.emergency_contact_name || '',
      emergency_contact_phone: member.emergency_contact_phone || '',
      date_of_birth: formatDateForInput(member.date_of_birth),
      membership_date: formatDateForInput(member.membership_date),
      expiry_date: formatDateForInput(member.expiry_date),
      is_committee_member: member.is_committee_member || false,
      is_department_head: isDepartmentHead,
      committee_position: member.committee_position || '',
      committee_description: member.committee_description || '',
      role: isDepartmentHead ? 'department_head' : 'member',
      department_ids: userDepartmentIds
    });
    setWhatsappSameAsPhone(isSame);
    setShowModal(true);
  };

  const handleDelete = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to delete this member?')) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/users/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setMembers(prev => prev.filter(member => member.id !== memberId));
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to delete member');
      }
    } catch (error) {
      console.error('Error deleting member:', error);
      alert('Network error. Please try again.');
    }
  };

  const getInitialCreateFormData = (): FormData => ({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    whatsapp_number: '',
    password: '',
    status: 'active',
    member_id: '',
    block_number: '',
    house_number: '',
    floor_number: '',
    address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    date_of_birth: '',
    membership_date: '',
    expiry_date: getLastDayOfCurrentFinancialYear(), // Default: last day of current FY (31 March)
    is_committee_member: false,
    is_department_head: false,
    committee_position: '',
    committee_description: '',
    role: 'member',
    department_ids: []
  });

  // Open edit/create from URL (?edit=uuid or ?action=create) after departments load
  useEffect(() => {
    if (!isUniversalAdmin || departments.length === 0) return;
    const editId = searchParams.get('edit');
    const action = searchParams.get('action');
    const deptFromUrl = searchParams.get('department_id');

    if (editId && deepLinkEditHandled.current !== editId) {
      deepLinkEditHandled.current = editId;
      (async () => {
        try {
          const response = await fetch(`/api/v1/users/${editId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.data) {
              await handleEdit(data.data as Member);
              setSearchParams(
                (prev) => {
                  const p = new URLSearchParams(prev);
                  p.delete('edit');
                  return p;
                },
                { replace: true }
              );
            }
          }
        } catch (e) {
          console.error('Deep link edit failed:', e);
        }
      })();
      return;
    }

    if (action === 'create' && deptFromUrl && !deepLinkCreateHandled.current) {
      deepLinkCreateHandled.current = true;
      setModalMode('create');
      setEditingMember(null);
      setFormData({
        ...getInitialCreateFormData(),
        department_ids: [deptFromUrl]
      });
      setWhatsappSameAsPhone(false);
      setShowModal(true);
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.delete('action');
          return p;
        },
        { replace: true }
      );
    }
  }, [isUniversalAdmin, departments.length, searchParams, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps -- handleEdit

  useEffect(() => {
    if (searchParams.get('action') !== 'create') deepLinkCreateHandled.current = false;
  }, [searchParams]);

  const closeModal = () => {
    setShowModal(false);
    setEditingMember(null);
    setModalMode('create');
    setFormData(getInitialCreateFormData());
    setWhatsappSameAsPhone(false);
    setErrors({});
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'universal_admin': return 'bg-purple-100 text-purple-800';
      case 'office_admin': return 'bg-blue-100 text-blue-800';
      case 'department_head': return 'bg-green-100 text-green-800';
      case 'front_worker': return 'bg-yellow-100 text-yellow-800';
      case 'member': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-yellow-100 text-yellow-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isUniversalAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Residential Members</h1>
              <p className="mt-2 text-gray-600">Manage residential members living in the society</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleExportExcel}
                disabled={isExporting}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <Download className="h-4 w-4 mr-2" />
                {isExporting ? 'Exporting…' : 'Download Excel'}
              </button>
              <button
                onClick={fetchMembers}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
                <button
                  onClick={() => {
                    setModalMode('create');
                    setEditingMember(null);
                    setFormData(getInitialCreateFormData());
                    setShowModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Member
                </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, email, member ID, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                <select
                  value={departmentFilterId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDepartmentFilterId(v);
                    setSearchParams(
                      (prev) => {
                        const p = new URLSearchParams(prev);
                        if (v) p.set('department_id', v);
                        else p.delete('department_id');
                        return p;
                      },
                      { replace: true }
                    );
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All departments</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Roles</option>
                  <option value="office_admin">Office Admin</option>
                  <option value="department_head">Department Head</option>
                  <option value="front_worker">Front Worker</option>
                  <option value="staff">Staff</option>
                  <option value="committee_member">Committee Member</option>
                  <option value="member">Member</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div className="lg:col-span-5 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setRoleFilter('all');
                    setStatusFilter('all');
                    setDepartmentFilterId('');
                    setSearchParams(
                      (prev) => {
                        const p = new URLSearchParams(prev);
                        p.delete('department_id');
                        return p;
                      },
                      { replace: true }
                    );
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Members Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              Members ({totalMembers.toLocaleString()})
              {searchTerm && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  (showing {startIndex.toLocaleString()}-{endIndex.toLocaleString()})
                </span>
              )}
            </h3>
            <div className="flex items-center gap-4">
              <label className="text-sm text-gray-700 flex items-center gap-2">
                Show:
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-12">
              <User className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No members found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || roleFilter !== 'all' || statusFilter !== 'all' || departmentFilterId
                  ? 'Try adjusting your filters to see more results.'
                  : 'Get started by creating a new member.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Member
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Member ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {members.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <User className="h-5 w-5 text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {member.first_name} {member.last_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {member.block_number && member.flat_number && 
                                `Block ${member.block_number}, House ${member.flat_number}`
                              }
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{member.email}</div>
                        <div className="text-sm text-gray-500">{member.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(member.role)}`}>
                            {member.role.replace('_', ' ').toUpperCase()}
                          </span>
                          {member.is_committee_member && member.committee_position && (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                              {member.committee_position}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(member.status)}`}>
                          {member.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {member.member_id || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(member)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(member.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {!isLoading && totalMembers > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex.toLocaleString()}</span> to{' '}
                    <span className="font-medium">{endIndex.toLocaleString()}</span> of{' '}
                    <span className="font-medium">{totalMembers.toLocaleString()}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Member Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {modalMode === 'create' ? 'Add New Residential Member' : 'Edit Residential Member'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* General Error */}
                {errors.general && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                      <p className="text-sm text-red-800">{errors.general}</p>
                    </div>
                  </div>
                )}

                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.first_name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="First name"
                    />
                    {errors.first_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.last_name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Last name"
                    />
                    {errors.last_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.email ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="email@example.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone *
                    </label>
                    <input
                      ref={phoneInputRef}
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.phone ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Phone number"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      WhatsApp Number
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="tel"
                        value={formData.whatsapp_number}
                        onChange={(e) => {
                          handleInputChange('whatsapp_number', e.target.value);
                          // If user manually changes WhatsApp, unlink from phone
                          if (whatsappSameAsPhone && e.target.value !== formData.phone) {
                            setWhatsappSameAsPhone(false);
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="WhatsApp number"
                      />
                      <button
                        type="button"
                        onClick={copyPhoneToWhatsApp}
                        disabled={!formData.phone}
                        className={`px-3 py-2 border rounded-md flex items-center justify-center ${
                          whatsappSameAsPhone 
                            ? 'bg-green-100 border-green-300 text-green-700 hover:bg-green-200' 
                            : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                        } disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                        title="Copy phone number to WhatsApp"
                      >
                        {whatsappSameAsPhone ? (
                          <Check className="h-5 w-5" />
                        ) : (
                          <Copy className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    {whatsappSameAsPhone && (
                      <p className="mt-1 text-xs text-green-600 flex items-center">
                        <Check className="h-3 w-3 mr-1" />
                        Using phone number for WhatsApp
                      </p>
                    )}
                  </div>

                  {modalMode === 'edit' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.password ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Leave blank to keep current password"
                      />
                      {errors.password && (
                        <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                      )}
                    </div>
                  )}
                  {modalMode === 'create' && (
                    <p className="text-sm text-gray-500">New members get default password: nfcrwa@123</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>

                  {/* Member ID is auto-generated by backend based on role */}
                  {modalMode === 'edit' && editingMember?.member_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Member ID
                      </label>
                      <input
                        type="text"
                        value={editingMember.member_id}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
                        disabled
                        readOnly
                      />
                      <p className="mt-1 text-xs text-gray-500">Member ID cannot be changed</p>
                    </div>
                  )}
                  {modalMode === 'create' && (
                    <div className="hidden">
                      {/* Member ID will be auto-generated by backend */}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Block Number *
                    </label>
                    <input
                      type="text"
                      value={formData.block_number}
                      onChange={(e) => handleInputChange('block_number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., A, B, C"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      House Number *
                    </label>
                    <input
                      type="text"
                      value={formData.house_number}
                      onChange={(e) => handleInputChange('house_number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 101, 102, 201"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Floor Number
                    </label>
                    <input
                      type="text"
                      value={formData.floor_number}
                      onChange={(e) => handleInputChange('floor_number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Ground, 1st, 2nd"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Address *
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Complete residential address including society name, area, city"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth *
                    </label>
                    <input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.date_of_birth ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required
                    />
                    {errors.date_of_birth && (
                      <p className="mt-1 text-sm text-red-600">{errors.date_of_birth}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Membership Date *
                    </label>
                    <input
                      type="date"
                      value={formData.membership_date}
                      onChange={(e) => handleInputChange('membership_date', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.membership_date ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required
                    />
                    {errors.membership_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.membership_date}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiry Date *
                    </label>
                    <input
                      type="date"
                      value={formData.expiry_date}
                      onChange={(e) => handleInputChange('expiry_date', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.expiry_date ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required
                    />
                    {errors.expiry_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.expiry_date}</p>
                    )}
                  </div>
                </div>

                {/* Role and Options */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => {
                      const newRole = e.target.value as 'member' | 'department_head';
                      handleInputChange('role', newRole);
                      // If role is changed to member, uncheck department head
                      if (newRole === 'member') {
                        handleInputChange('is_department_head', false);
                        handleInputChange('department_ids', []);
                      } else if (newRole === 'department_head') {
                        handleInputChange('is_department_head', true);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="member">Member</option>
                    <option value="department_head">Department Head</option>
                  </select>
                </div>

                {/* Committee Member and Department Head Checkboxes */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => {
                        handleInputChange('is_committee_member', !formData.is_committee_member);
                      }}
                      className={`flex items-center justify-center w-6 h-6 border-2 rounded transition-colors ${
                        formData.is_committee_member
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white border-gray-300 text-gray-400 hover:border-blue-400'
                      } cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1`}
                      title={formData.is_committee_member ? 'Uncheck Committee Member' : 'Check Committee Member'}
                    >
                      {formData.is_committee_member && (
                        <Check className="h-4 w-4" />
                      )}
                    </button>
                    <label 
                      onClick={() => {
                        handleInputChange('is_committee_member', !formData.is_committee_member);
                      }}
                      className="ml-2 text-sm text-gray-700 cursor-pointer select-none"
                    >
                      Committee Member
                    </label>
                  </div>

                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => {
                        handleInputChange('is_department_head', !formData.is_department_head);
                        // If setting as department head, ensure role is set correctly
                        if (!formData.is_department_head) {
                          handleInputChange('role', 'department_head');
                        } else {
                          // If unchecking, set role to member and clear departments
                          handleInputChange('role', 'member');
                          handleInputChange('department_ids', []);
                        }
                      }}
                      className={`flex items-center justify-center w-6 h-6 border-2 rounded transition-colors ${
                        formData.is_department_head
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white border-gray-300 text-gray-400 hover:border-blue-400'
                      } cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1`}
                      title={formData.is_department_head ? 'Uncheck Department Head' : 'Check Department Head'}
                    >
                      {formData.is_department_head && (
                        <Check className="h-4 w-4" />
                      )}
                    </button>
                    <label 
                      onClick={() => {
                        handleInputChange('is_department_head', !formData.is_department_head);
                        // If setting as department head, ensure role is set correctly
                        if (!formData.is_department_head) {
                          handleInputChange('role', 'department_head');
                        } else {
                          // If unchecking, set role to member and clear departments
                          handleInputChange('role', 'member');
                          handleInputChange('department_ids', []);
                        }
                      }}
                      className="ml-2 text-sm text-gray-700 cursor-pointer select-none"
                    >
                      Department Head
                    </label>
                  </div>
                </div>

                {/* Department Selection for Department Head */}
                {(formData.is_department_head || formData.role === 'department_head') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assign Departments *
                    </label>
                    <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3 bg-white">
                      {departments.length === 0 ? (
                        <p className="text-sm text-gray-500">No departments available</p>
                      ) : (
                        departments.map((dept) => {
                          const isChecked = formData.department_ids.includes(dept.id);
                          return (
                            <div 
                              key={dept.id} 
                              className="flex items-center py-2 hover:bg-gray-50"
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  let newDepartmentIds: string[];
                                  if (isChecked) {
                                    newDepartmentIds = formData.department_ids.filter(id => id !== dept.id);
                                  } else {
                                    newDepartmentIds = [...formData.department_ids, dept.id];
                                  }
                                  
                                  handleInputChange('department_ids', newDepartmentIds);
                                  
                                  // If all departments are removed, automatically change role to member
                                  if (newDepartmentIds.length === 0 && (formData.role === 'department_head' || formData.is_department_head)) {
                                    handleInputChange('role', 'member');
                                    handleInputChange('is_department_head', false);
                                  } else if (newDepartmentIds.length > 0 && formData.role === 'member') {
                                    // If departments are added and role is member, change to department_head
                                    handleInputChange('role', 'department_head');
                                    handleInputChange('is_department_head', true);
                                  }
                                }}
                                className={`flex items-center justify-center w-6 h-6 border-2 rounded transition-colors ${
                                  isChecked
                                    ? 'bg-blue-600 border-blue-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-400 hover:border-blue-400'
                                } cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1`}
                                title={isChecked ? 'Unselect department' : 'Select department'}
                              >
                                {isChecked && (
                                  <Check className="h-4 w-4" />
                                )}
                              </button>
                              <label 
                                onClick={() => {
                                  let newDepartmentIds: string[];
                                  if (isChecked) {
                                    newDepartmentIds = formData.department_ids.filter(id => id !== dept.id);
                                  } else {
                                    newDepartmentIds = [...formData.department_ids, dept.id];
                                  }
                                  
                                  handleInputChange('department_ids', newDepartmentIds);
                                  
                                  // If all departments are removed, automatically change role to member
                                  if (newDepartmentIds.length === 0 && (formData.role === 'department_head' || formData.is_department_head)) {
                                    handleInputChange('role', 'member');
                                    handleInputChange('is_department_head', false);
                                  } else if (newDepartmentIds.length > 0 && formData.role === 'member') {
                                    // If departments are added and role is member, change to department_head
                                    handleInputChange('role', 'department_head');
                                    handleInputChange('is_department_head', true);
                                  }
                                }}
                                className="ml-2 text-sm text-gray-700 cursor-pointer select-none"
                              >
                                {dept.name} ({dept.code})
                              </label>
                            </div>
                          );
                        })
                      )}
                    </div>
                    {(formData.is_department_head || formData.role === 'department_head') && formData.department_ids.length === 0 && (
                      <p className="mt-1 text-sm text-red-600">At least one department must be selected for department head</p>
                    )}
                  </div>
                )}

                {formData.is_committee_member && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Committee Position
                      </label>
                      <select
                        value={formData.committee_position}
                        onChange={(e) => handleInputChange('committee_position', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select position</option>
                        {COMMITTEE_ROLES.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Committee Description
                      </label>
                      <input
                        type="text"
                        value={formData.committee_description}
                        onChange={(e) => handleInputChange('committee_description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Committee description"
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {modalMode === 'create' ? 'Add Member' : 'Update Member'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersPage;
